import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Mail, MessageCircle, Send, Clock, Instagram, Facebook } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // UI only - show success message
    setIsSubmitted(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 7899162820',
      href: 'tel:7899162820',
    },
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      value: 'Message us',
      href: 'https://wa.me/917899162820?text=Hi%20SDM!%20I%20want%20to%20know%20more%20about%20training.',
      isWhatsApp: true,
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'pramodlaxman1721@gmail.com',
      href: 'mailto:pramodlaxman1721@gmail.com',
    },
    {
      icon: Clock,
      label: 'Training Hours',
      value: '6 AM - 9 PM',
      href: null,
    },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
            Contact Us
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Get In<br />
            <span className="text-[#DC2626]">Touch</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Ready to start your martial arts journey? Contact us today or visit our facility in Mysuru.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Contact Cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  target={item.href?.startsWith('http') ? '_blank' : undefined}
                  rel={item.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className={`p-6 bg-[#121212] border border-[#262626] hover:border-[#DC2626]/50 transition-all group ${
                    item.isWhatsApp ? 'whatsapp-btn' : ''
                  } ${!item.href ? 'pointer-events-none' : ''}`}
                  data-testid={`contact-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      item.isWhatsApp ? 'bg-green-600' : 'bg-[#1A1A1A]'
                    } group-hover:bg-[#DC2626] transition-colors`}>
                      <item.icon size={18} className="text-white" />
                    </div>
                    <span className="text-neutral-500 text-xs uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                  <p className="text-white font-medium">{item.value}</p>
                </motion.a>
              ))}
            </div>

            {/* Location */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={20} className="text-[#DC2626]" />
                <h3 className="font-['Oswald'] text-white text-lg uppercase tracking-wide">
                  Our Location
                </h3>
              </div>
              <p className="text-neutral-400 mb-4">
                #291/C Hebbal Industrial Area,<br />
                Near Aroma Bakery, Opp to SPI,<br />
                Mysore, Karnataka, India - 570016
              </p>
            </div>

            {/* Google Map */}
            <div className="relative overflow-hidden border border-[#262626]">
              <div className="absolute inset-0 pointer-events-none z-10 border-4 border-[#050505]/50" />
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3897.8!2d76.6!3d12.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sHebbal%20Industrial%20Area%2C%20Mysore%2C%20Karnataka%20570016!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map-grayscale"
                title="SDM MMA Location - Hebbal Industrial Area, Mysore"
              />
            </div>

            {/* Social Links */}
            <div className="mt-6 flex items-center gap-4">
              <span className="text-neutral-500 text-sm">Follow us:</span>
              <a
                href="https://www.instagram.com/sdm_mysuru"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-[#121212] border border-[#262626] hover:border-[#DC2626] hover:bg-[#DC2626] transition-all"
                data-testid="social-instagram"
              >
                <Instagram size={18} className="text-white" />
              </a>
              <a
                href="#"
                className="w-10 h-10 flex items-center justify-center bg-[#121212] border border-[#262626] hover:border-[#DC2626] hover:bg-[#DC2626] transition-all"
                data-testid="social-facebook"
              >
                <Facebook size={18} className="text-white" />
              </a>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="bg-[#121212] border border-[#262626] p-8">
              <h3 className="font-['Oswald'] text-2xl text-white font-bold uppercase tracking-wide mb-6">
                Send Us a Message
              </h3>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[#DC2626] rounded-full">
                    <Send size={28} className="text-white" />
                  </div>
                  <h4 className="text-white text-xl font-bold mb-2">Message Sent!</h4>
                  <p className="text-neutral-400">We'll get back to you soon.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">Your Name</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-neutral-600 focus:border-[#DC2626] rounded-none h-12"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-neutral-400 text-sm mb-2">Email</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-neutral-600 focus:border-[#DC2626] rounded-none h-12"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-neutral-400 text-sm mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-neutral-600 focus:border-[#DC2626] rounded-none h-12"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="I'm interested in joining the MMA program..."
                      rows={5}
                      className="bg-[#0A0A0A] border-[#262626] text-white placeholder:text-neutral-600 focus:border-[#DC2626] rounded-none resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-skew bg-[#DC2626] text-white font-bold uppercase tracking-wider py-6 hover:bg-[#B91C1C] transition-all rounded-none"
                    data-testid="contact-submit-btn"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Send size={18} className="transform skew-x-[10deg]" />
                      Send Message
                    </span>
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
